import { ReactNode } from "react";
import LogoPurpleCircle from "../public/logo-purple-circle.svg";
import { Separator } from "./shadcn/ui/separator";

function AuthColumn({
  children,
  showUserAgreement,
  showDocs,
}: {
  children: ReactNode;
  showUserAgreement: boolean;
  showDocs: boolean;
}) {
  return (
    <div className="flex flex-col items-center max-w-[350px] w-full">
      <a href="https://www.osohq.com" target="_blank" rel="noreferrer">
        <LogoPurpleCircle
          role="img"
          aria-label="Oso Logo"
          className="rounded-full shadow-xl grow-0 hover:scale-105 transition-transform"
        />
      </a>
      <div className="mt-6 font-space text-gray-800 dark:text-foreground text-2xl text-center">
        Oso for Agents
      </div>
      <div className="mt-6 min-h-[7rem]">
        <div className="flex flex-col space-y-4 w-full">{children}</div>
        <div className="mt-6 text-gray-500 dark:text-muted-foreground text-center text-sm space-y-2">
          {showUserAgreement && (
            <p>
              By signing in, you agree to the{" "}
              <a
                href="https://www.osohq.com/user-agreement"
                className="text-primary hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                User Agreement
              </a>
              .
            </p>
          )}
          {showDocs && (
            <p>
              Have questions?{" "}
              <a
                href="https://www.osohq.com/docs/oso-for-agents/overview"
                className="text-primary hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                Read our docs.
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ApertureAuthLayout({
  children,
  showUserAgreement = false,
  showDocs = false,
}: {
  children: ReactNode;
  showUserAgreement?: boolean;
  showDocs?: boolean;
}) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col lg:flex-row">
      {/* Left column: explainer on Oso purple gradient (dark bottom-left -> light upper-right) */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:py-16 lg:px-12 xl:px-16 bg-gradient-to-tr from-oso-purple-dark via-oso-purple to-oso-purple-light">
        <div className="max-w-lg mx-auto lg:mx-0 w-full">
          <h1 className="font-space text-2xl text-primary-foreground">
            Connect
            <img
              src="/tailscale-logo.svg"
              alt="Tailscale"
              className="inline h-6 w-auto align-middle -mt-1 mx-3"
            />
          </h1>
          <p className="mt-3 text-primary-foreground/90">
            Visibility, controls, alerting, and auditing for Claude Code,
            Cursor, Codex, and more.
          </p>

          <div className="mt-8">
            <h2 className="font-space text-xl text-primary-foreground">
              How it works
            </h2>
            <ol className="mt-3 space-y-3">
              <li className="flex items-center gap-3 text-base text-primary-foreground/90">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground font-medium text-sm">
                  1
                </span>
                Aperture routes AI traffic to Oso.
              </li>
              <li className="flex items-center gap-3 text-base text-primary-foreground/90">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground font-medium text-sm">
                  2
                </span>
                Oso analyzes tool use and flags anything suspicious.
              </li>
              <li className="flex items-center gap-3 text-base text-primary-foreground/90">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground font-medium text-sm">
                  3
                </span>
                You get alerts ready for triage.
              </li>
            </ol>
          </div>
        </div>
      </div>

      <Separator
        orientation="vertical"
        className="hidden lg:block h-auto self-stretch"
      />

      {/* Right column: auth */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:py-16">
        <AuthColumn showUserAgreement={showUserAgreement} showDocs={showDocs}>
          {children}
        </AuthColumn>
      </div>
    </div>
  );
}
